import { Component, Prop, h, Element } from '@stencil/core';

/**
 * `me-heading` is a semantic heading component that allows customizable
 * variant, alignment, and HTML tag rendering (e.g. h1–h6 or div).
 */

const COMPONENT_NAME = 'me-heading';
const DEFAULTS = {
  align: 'left',
} as const;

/**
 * @slot - {required}, string
 */
@Component({
  tag: 'me-heading',
  styleUrl: 'index.scss',
  shadow: false,
})
export class Heading {
  /** Reference to the host element (<me-heading>) */
  @Element() host: HTMLElement;

  /**
   * The HTML tag used to render the heading.
   * This determines semantic importance (h1–h6) or neutral (`div`).
   * @default "div"
   */
  @Prop() tag: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'div' = 'div';

  /**
   * Visual variant of the heading.
   * Typically corresponds to font size, spacing, and visual hierarchy (1–6).
   * This value is used to generate a CSS modifier class.
   */
  @Prop() variant: '1' | '2' | '3' | '4' | '5' | '6';

  /**
   * Text alignment of the heading: left, center, or right.
   * @default "left"
   */
  @Prop() align: 'left' | 'right' | 'center' = DEFAULTS.align;

  /**
   * Extracts all attributes from the host element that are not known props,
   * so they can be passed through to the rendered tag.
   *
   * This allows support for native attributes like `id`, `data-*`, `aria-*`, `itemprop`, etc.
   */
  private getExtraAttributes(): Record<string, any> {
    const knownProps = ['tag', 'variant', 'align', 'class'];
    const attrs: Record<string, any> = {};

    Array.from(this.host.attributes).forEach(attr => {
      const attrName = attr.name.toLowerCase();
      if (!knownProps.includes(attrName)) {
        // Stencil converts camelCase JSX attributes to lowercase HTML attributes
        // e.g., itemProp -> itemprop, itemScope -> itemscope, itemType -> itemtype
        // So we can use the attribute name as-is
        attrs[attr.name] = attr.value;
      }
    });

    return attrs;
  }

  render() {
    const Tag = this.tag;

    // Apply variant class if a value is provided
    const variantClass = this.variant ? `${COMPONENT_NAME}--${this.variant}` : '';

    // Apply alignment class only if it's different from the default
    const alignClass = this.align !== DEFAULTS.align ? `${COMPONENT_NAME}--${this.align}` : '';

    // Build final list of classes, omitting falsy values
    const classList = [COMPONENT_NAME, variantClass, alignClass].filter(Boolean).join(' ');

    // Get extra attributes to pass through (e.g., itemprop, itemscope, etc.)
    const extraAttrs = this.getExtraAttributes();

    // Remove itemprop from host element after passing it to rendered element
    // This prevents duplicate itemprop attributes (one on <me-heading>, one on rendered element)
    if (this.host.hasAttribute('itemprop')) {
      this.host.removeAttribute('itemprop');
    }

    // Render the specified HTML tag with the calculated classes, extra attributes, and slot content
    return (
      <Tag class={classList} {...extraAttrs}>
        <slot></slot>
      </Tag>
    );
  }
}
